/**
 * Token management utility
 * 独立的 token 管理模块，可以在任何地方安全使用
 * 不依赖于 Vue composables，避免时序和上下文问题
 */

const TOKEN_KEY = 'auth_token';

/**
 * 检查是否在客户端环境
 * 使用多重检查确保在 Nuxt SSR 环境中正确判断
 */
function isClient(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function getCookie(name: string) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
}

export function getTokenFromCookie(): string | undefined {
  if (!isClient()) {
    return undefined;
  }
  try {
    return getCookie(TOKEN_KEY);
  } catch (e) {
    console.warn('Failed to get token from cookie:', e);
    return undefined;
  }
}

/**
 * 获取 token
 * @returns token 或 null
 */
export function getToken(): string | null {
  if (!isClient()) {
    return null;
  }
  try {
    return localStorage.getItem(TOKEN_KEY) ?? getTokenFromCookie() ?? null;
  } catch (e) {
    console.warn('Failed to get token from localStorage:', e);
    return null;
  }
}

/**
 * 设置 token
 * @param token - 要存储的 token
 */
export function setToken(token: string): void {
  if (!isClient()) {
    return;
  }

  const maxAge = 60 * 60 * 24 * 7;
  const domains = [
    location.hostname,
    location.hostname.endsWith('veeso.ai') ? 'veeso.ai' : undefined,
  ].filter(Boolean);

  try {
    localStorage.setItem(TOKEN_KEY, token);
    domains.forEach((domain) => {
      document.cookie = `${TOKEN_KEY}=${token}; domain=${domain}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;
    });
  } catch (e) {
    console.warn('Failed to set token to localStorage:', e);
  }
}

/**
 * 移除 token
 */
export function removeToken(): void {
  if (!isClient()) {
    return;
  }
  const domains = [
    location.hostname,
    location.hostname.endsWith('veeso.ai') ? 'veeso.ai' : undefined,
  ].filter(Boolean);

  try {
    domains.forEach((domain) => {
      document.cookie = `${TOKEN_KEY}=; domain=${domain}; path=/; max-age=0; SameSite=Lax; Secure`;
    });
    localStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    console.warn('Failed to remove token from localStorage:', e);
  }
}

/**
 * 检查是否有 token
 * @returns boolean
 */
export function hasToken(): boolean {
  return !!getToken();
}
